import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type {
  AnalyticsEvent,
  ChangeCause,
  DiscoveryFacts,
  DiscoverySummary,
  Evidence,
  ExportDraft,
  ExportRecord,
  HistoryEvent,
  IndexDef,
  LevelBasis,
  LevelCode,
  ObjectiveEvent,
  ObjectiveState,
  PlaybookFacts,
  RecommendationLink,
  Snapshot,
  SnapshotItem,
  SyncChange,
  SyncRun,
  Transition,
} from '@/api/types/readiness'
import { levelNum } from '@/api/types/readiness'
import { getIndex, allObjectives, levelName, DEFAULT_INDEX_ID, tierCount } from '@/lib/indexes/registry'
import { suggestLevel, suggestTier } from '@/lib/readiness/signals'
import { computeScore, computeIndexScore, liveLevel, liveScore, reportedLevel, reportedScore, tierScore } from '@/lib/readiness/score'
import { assertScale } from '@/lib/readiness/scale'
import { EMPTY_PLAYBOOK_FACTS, mergePlaybookFacts, playbookFactsFromActions, type LocalPlaybookAction } from '@/lib/readiness/facts'
import { evidenceFromFacts } from '@/lib/readiness/evidence'
import { DEFAULT_SCOPE_THRESHOLD, emptyScope, emptyState, nextScheduledSnapshot, scopeKey, stepKey, type ScopeData, type ScopeSettings, type StepChoice } from '@/lib/readiness/scope'
import { objectiveHasEstateScope } from '@/lib/catalogue/engine'
import { uid } from '@/lib/readiness/util'

// Scopes persisted against the withdrawn demo tenant, dropped by migrate.
const RETIRED_DEMO_SCOPE_PREFIX = 'demo-org::'

// ── Local readiness engine ──────────────────────────────────────────────────
// Implements the behaviour of the Iteration 1 and 2 briefs (sync, suggestions,
// confirmation, evidence, the objective event log, assessments with review
// gates, Playbook links, export drafts) in the browser, persisted per org ×
// index. The record shapes mirror the tables in Engineering Note 2 so the same
// UI can be pointed at the qtz backend once the `/readiness/*` endpoints exist
// (see src/readiness/api/readinessApi.ts).

export { emptyScope, emptyState, nextScheduledSnapshot, scopeKey, uid }
export type { ScopeData, ScopeSettings }

const now = () => new Date().toISOString()

export type ViewMode = 'cards' | 'heatmap'

// ── Review gates (I2 §8, Note 5) ────────────────────────────────────────────
// Run in the modal and again on save; a save that fails a blocking check
// returns the failing codes.

export interface NeedsAttentionRow {
  code: string
  suggested_level: LevelCode | null
  suggested_score: number | null
  recorded_level: LevelCode
  recorded_score: number | null
  last_confirmed_at: string | null
}

export interface BlockingCheck {
  code: string
  kind: 'l3_no_evidence' | 'changed_no_note'
  text: string
}

export interface ReviewGates {
  needs_attention: NeedsAttentionRow[]
  blocking: BlockingCheck[]
}

export function reviewGates(index: IndexDef, scope: ScopeData): ReviewGates {
  const last = scope.snapshots[scope.snapshots.length - 1]
  const needs_attention: NeedsAttentionRow[] = []
  const blocking: BlockingCheck[] = []
  const scale = assertScale(index)
  allObjectives(index).forEach(({ objective }) => {
    const code = objective.code
    const st = scope.states[code]
    if (!st) return
    const suggestedPending = st.status === 'suggested' || (st.suggested_level != null && st.suggested_level !== st.level) || (scale === 'score_0_10' && st.suggested_score != null && st.suggested_score !== st.score)
    if (suggestedPending) {
      needs_attention.push({
        code,
        suggested_level: st.suggested_level,
        suggested_score: st.suggested_score,
        recorded_level: reportedLevel(st),
        recorded_score: reportedScore(st),
        last_confirmed_at: st.status === 'confirmed' || st.status === 'declared' ? st.set_at : null,
      })
    }
    const evidence = scope.evidence.filter((e) => e.objective_code === code && !e.archived_at).length
    const recorded = reportedLevel(st)
    if (scale === 'levels' && recorded === 'L3' && evidence === 0) blocking.push({ code, kind: 'l3_no_evidence', text: `${code} is at L3 with no evidence` })
    if (scale === 'score_0_10' && (reportedScore(st) ?? 0) >= 10 && evidence === 0) blocking.push({ code, kind: 'l3_no_evidence', text: `${code} is at 10.0 with no evidence` })
    if (last && (st.status === 'confirmed' || st.status === 'declared')) {
      const prev = last.items.find((i) => i.objective_code === code)
      const changed = scale === 'levels' ? (prev?.level ?? 'L0') !== recorded : (prev?.score ?? null) !== (st.score ?? null)
      const setSince = !!st.set_at && st.set_at > last.created_at
      // Only a *declared* (user-set) change needs a note; confirming a system
      // suggestion carries its reason.
      if (changed && setSince && st.status === 'declared' && !st.note) blocking.push({ code, kind: 'changed_no_note', text: `${code} changed since ${last.name} with no note` })
    }
  })
  return { needs_attention, blocking }
}

export class ReviewGateError extends Error {
  constructor(public codes: string[], public checks: BlockingCheck[]) {
    super(`Blocking checks failed: ${codes.join(', ')}`)
  }
}

export function discoverySummaryOf(facts: DiscoveryFacts | null, lastScanAt: string | null): DiscoverySummary | null {
  if (!facts || !facts.connected) return null
  const qr = facts.quantum_risk
  const safe = qr?.safe ?? 0
  const total = facts.asset_count || (qr ? qr.vulnerable + qr.partial + qr.hybrid + qr.safe + qr.unknown : 0)
  return { assets_total: total, assets_quantum_safe: safe, assets_not_migrated: Math.max(0, total - safe), sources: facts.source_count, last_scan_at: lastScanAt }
}

interface ReadinessStore {
  selectedIndexId: string
  syncIndex: number // running count of sync runs (readiness_sync_run.index)
  viewMode: ViewMode // Overview card / heatmap toggle, persisted per user (I2 §3)
  scopes: Record<string, ScopeData>
  setIndex: (id: string) => void
  setViewMode: (mode: ViewMode) => void
  // Creates the scope if missing.
  ensureScope: (orgId: string, indexId: string) => void

  runSync: (
    orgId: string,
    indexId: string,
    facts: { discovery: DiscoveryFacts; playbook?: PlaybookFacts },
    trigger: SyncRun['trigger'],
    actor: string
  ) => SyncRun
  failSync: (orgId: string, indexId: string, error: string, trigger: SyncRun['trigger']) => void

  confirmLevel: (
    orgId: string,
    indexId: string,
    code: string,
    level: LevelCode,
    note: string,
    actor: string,
    cause?: ChangeCause
  ) => void
  // Scale score_0_10: set a tier (score derived) or a different score with a note.
  confirmScore: (
    orgId: string,
    indexId: string,
    code: string,
    input: { tier: number | null; score: number },
    note: string,
    actor: string
  ) => void
  setDetails: (
    orgId: string,
    indexId: string,
    code: string,
    patch: Partial<Pick<ObjectiveState, 'owner' | 'target_level_text' | 'due_at'>>,
    actor: string
  ) => void
  setBasis: (orgId: string, indexId: string, code: string, basis: LevelBasis, actor: string) => void
  // Uploads the PDF CSA's official questionnaire produced as evidence and sets the basis (I2 §6).
  attachCsaReport: (orgId: string, indexId: string, code: string, fileName: string, actor: string) => Evidence

  addEvidence: (
    orgId: string,
    indexId: string,
    input: {
      objective_code: string
      title: string
      summary: string
      link: string | null
      occurred_at: string
      kind: 'file' | 'note'
      file_name?: string | null
      suggest_level?: LevelCode | null
    },
    actor: string
  ) => Evidence
  // Iteration 5 §13: any source can be archived; the write is deferred by the
  // UI until the undo window closes. Restore brings an archived item back.
  archiveEvidence: (orgId: string, indexId: string, evidenceId: string, actor: string) => void
  restoreEvidence: (orgId: string, indexId: string, evidenceId: string, actor: string) => void
  // Iteration 5 §16: the migration / catalogue sync adds its own changes to the
  // readiness run that triggered it; `markSyncViewed` clears the strip link.
  appendSyncChanges: (orgId: string, indexId: string, runId: string, changes: SyncChange[]) => void
  syncViewed: Record<string, string> // run id → viewed_at (this user's browser)
  markSyncViewed: (runId: string) => void

  // Save an assessment (I2 §8). Throws ReviewGateError when a blocking check fails.
  saveSnapshot: (
    orgId: string,
    indexId: string,
    input: { name: string; note: string | null; scheduled?: boolean; as_of?: string | null; force?: boolean; projection?: Snapshot['projection'] },
    actor: string
  ) => Snapshot
  setSnapshotRule: (orgId: string, indexId: string, rule: ScopeSettings['snapshot_rule']) => void
  // Dismiss the save reminder until the next scheduled date (I2 §1.2).
  snoozeReminder: (orgId: string, indexId: string) => void

  createPlaybookAction: (
    orgId: string,
    indexId: string,
    input: {
      objective_code: string
      transition: Transition
      bullet_index: number
      title: string
      owner: string | null
      due_at: string | null
      inventory_id?: string | null
    },
    actor: string
  ) => RecommendationLink
  setLinkStatus: (orgId: string, indexId: string, linkId: string, status: RecommendationLink['status'], actor: string) => void

  upsertDraft: (orgId: string, indexId: string, draft: ExportDraft) => void
  deleteDraft: (orgId: string, indexId: string, draftId: string) => void
  recordExport: (orgId: string, indexId: string, rec: Omit<ExportRecord, 'id' | 'org_id' | 'index_id'>) => ExportRecord
  track: (orgId: string, indexId: string, ev: AnalyticsEvent) => void
  resetScope: (orgId: string, indexId: string) => void

  // Iteration 3 §12: a catalogue write moved, detected or readied a dependency
  // for these objectives. Returns the events written (for notifications).
  recordDependencyChange: (orgId: string, indexId: string, input: { codes: string[]; dependency: NonNullable<ObjectiveEvent['dependency']>; text: string }) => ObjectiveEvent[]
  setStepChoice: (orgId: string, indexId: string, code: string, transition: string, bullet: number, choice: Omit<StepChoice, 'at' | 'by'> | null, actor: string) => void
  setScopeThreshold: (orgId: string, indexId: string, threshold: { pct: number; abs: number }) => void
}

function withScope(
  scopes: Record<string, ScopeData>,
  orgId: string,
  indexId: string,
  fn: (s: ScopeData) => ScopeData
): Record<string, ScopeData> {
  const key = scopeKey(orgId, indexId)
  const cur = scopes[key] ?? emptyScope()
  return { ...scopes, [key]: fn(cur) }
}

function pushHistory(s: ScopeData, ev: Omit<HistoryEvent, 'id' | 'org_id' | 'index_id'>, orgId: string, indexId: string): ScopeData {
  return {
    ...s,
    history: [{ id: uid('h'), org_id: orgId, index_id: indexId, ...ev }, ...s.history].slice(0, 2000),
  }
}

// Every write to the objective state or evidence emits one event in the same
// transaction (I2 §2.2). The log is append-only and kept oldest first.
type EventInput = Partial<ObjectiveEvent> & Pick<ObjectiveEvent, 'event_type' | 'actor' | 'occurred_at'>
function pushEvent(s: ScopeData, ev: EventInput, orgId: string, indexId: string): ScopeData {
  const full: ObjectiveEvent = {
    id: uid('evt'),
    org_id: orgId,
    index_id: indexId,
    objective_code: null,
    from_level: null,
    to_level: null,
    score_from: null,
    score_to: null,
    evidence_id: null,
    note: null,
    ...ev,
  }
  return { ...s, events: [...(s.events ?? []), full] }
}

// ── Sync: evidence from facts, then suggestions from signals (§7, Note 4) ──
// Evidence creation lives in ./evidence.ts.

export const useReadinessStore = create<ReadinessStore>()(
  persist(
    (set, get) => ({
      selectedIndexId: DEFAULT_INDEX_ID,
      syncIndex: 0,
      syncViewed: {},
      viewMode: 'cards',
      scopes: {},

      setIndex: (id) => set({ selectedIndexId: id }),
      setViewMode: (mode) => set({ viewMode: mode }),

      ensureScope: (orgId, indexId) => {
        const key = scopeKey(orgId, indexId)
        if (!get().scopes[key]) set((st) => ({ scopes: { ...st.scopes, [key]: emptyScope() } }))
      },

      runSync: (orgId, indexId, facts, trigger, actor) => {
        const index = getIndex(indexId)
        const scale = assertScale(index)
        const startedAt = now()
        const changes: SyncChange[] = []
        let skippedArchived = 0
        let run: SyncRun = {
          id: uid('sync'),
          org_id: orgId,
          index_id: indexId,
          index: get().syncIndex + 1,
          started_at: startedAt,
          finished_at: null,
          status: 'running',
          error: null,
          discovery_connected: facts.discovery.connected,
          playbook_connected: facts.playbook?.service_connected ?? false,
          evidence_created: 0,
          suggestions_written: 0,
          trigger,
        }
        set((st) => ({
          syncIndex: st.syncIndex + 1,
          scopes: withScope(st.scopes, orgId, indexId, (s) => {
            // The service facts (Issuing CAs, remediation) come with the sync or
            // stay as last seen; the plans are always the local actions.
            const playbook = mergePlaybookFacts(facts.playbook ?? s.settings.playbook_facts ?? EMPTY_PLAYBOOK_FACTS, playbookFactsFromActions(s.playbook_actions))
            // 1. Evidence, idempotent on (source, external_id).
            //    An item a user archived stays archived even when its fact still
            //    holds (I5 §13.2): matched by its source fact key, counted for the summary.
            const existingKeys = new Set(s.evidence.map((e) => `${e.source}|${e.external_id}`))
            const archivedKeys = new Set(s.evidence.filter((e) => e.archived_at).map((e) => `${e.source}|${e.external_id}`))
            const candidates = evidenceFromFacts(orgId, indexId, { discovery: facts.discovery, playbook }, index)
            skippedArchived = candidates.filter((e) => archivedKeys.has(`${e.source}|${e.external_id}`)).length
            const fresh = candidates.filter((e) => !existingKeys.has(`${e.source}|${e.external_id}`))
            let next: ScopeData = { ...s, evidence: [...s.evidence, ...fresh] }
            const perObjective = new Map<string, { count: number; source: Evidence['source'] }>()
            fresh.forEach((e) => {
              next = pushHistory(next, { objective_code: e.objective_code, kind: e.source === 'playbook' ? 'playbook' : 'evidence', text: `Evidence added by sync · ${e.title}`, actor: 'system', at: e.occurred_at }, orgId, indexId)
              next = pushEvent(next, { objective_code: e.objective_code, event_type: 'evidence_added', evidence_id: e.id, actor: 'system', occurred_at: e.occurred_at }, orgId, indexId)
              const cur = perObjective.get(e.objective_code) ?? { count: 0, source: e.source }
              perObjective.set(e.objective_code, { count: cur.count + 1, source: e.source })
            })
            perObjective.forEach((v, code) => changes.push({ group: 'evidence', objective_code: code, count: v.count, source: v.source }))
            // 1b. Coverage (I3 §4.1): the known asset count for the estate grew by
            //     more than the threshold within this sync → `scope_expanded` on
            //     every objective whose scope is the estate.
            const prevCount = s.settings.discovery_facts?.connected ? s.settings.discovery_facts.asset_count : null
            const newCount = facts.discovery.connected ? facts.discovery.asset_count : null
            const th = s.settings.scope_threshold ?? DEFAULT_SCOPE_THRESHOLD
            const expanded = prevCount != null && newCount != null && newCount - prevCount > Math.min((prevCount * th.pct) / 100, th.abs)
            if (expanded) {
              changes.push({ group: 'coverage', from: prevCount, to: newCount })
              allObjectives(index).filter(({ objective }) => objectiveHasEstateScope(objective.code)).forEach(({ objective }) => {
                const ev = { objective_code: objective.code, event_type: 'scope_expanded' as const, actor: 'system', occurred_at: now(), assets_known_from: prevCount, assets_known_to: newCount, source: 'discovery_sync' }
                next = pushEvent(next, ev, orgId, indexId)
                const evId = next.events[next.events.length - 1].id
                next = pushHistory(next, { objective_code: objective.code, kind: 'coverage', text: `Scope expanded · ${prevCount.toLocaleString()} → ${newCount.toLocaleString()} assets known (+${(newCount - prevCount).toLocaleString()})`, actor: 'system', at: ev.occurred_at, event_id: evId }, orgId, indexId)
              })
            }
            // 2. Suggestions. Never lower a confirmed/declared level; write differing
            //    suggestions into suggested_level and leave level alone.
            let written = 0
            const states = { ...next.states }
            const ctx = { discovery: facts.discovery, playbook, evidence: next.evidence.filter((e) => !e.archived_at), assessments: next.snapshots.length }
            allObjectives(index).forEach(({ objective }) => {
              const code = objective.code
              const cur = states[code] ?? emptyState(orgId, indexId, code)
              const at = now()
              if (scale === 'score_0_10') {
                const sug = suggestTier(code, ctx)
                if (sug.manual_only) {
                  states[code] = cur
                  return
                }
                const m = tierCount(index, code)
                const score = sug.tier != null ? tierScore(sug.tier, m) : null
                const changed = cur.suggested_score !== score
                if (cur.status === 'unset' || cur.status === 'suggested') {
                  if (changed) {
                    written++
                    next = pushHistory(next, { objective_code: code, kind: 'suggestion', text: score != null ? `System suggested tier ${sug.tier} of ${m} · ${score.toFixed(1)}` : 'System withdrew its suggestion', actor: 'system', at }, orgId, indexId)
                    next = pushEvent(next, { objective_code: code, event_type: 'suggested', score_from: cur.suggested_score, score_to: score, tier: sug.tier, actor: 'system', occurred_at: at, reason: sug.reason }, orgId, indexId)
                  }
                  states[code] = score != null ? { ...cur, status: 'suggested', suggested_score: score, suggested_tier: sug.tier, suggested_at: at, suggested_reason: sug.reason } : { ...cur, status: 'unset', suggested_score: null, suggested_tier: null, suggested_at: null, suggested_reason: sug.reason }
                } else if (score != null && (cur.score ?? 0) < score) {
                  if (changed) {
                    written++
                    next = pushHistory(next, { objective_code: code, kind: 'suggestion', text: `Scored ${(cur.score ?? 0).toFixed(1)} · system now suggests ${score.toFixed(1)}`, actor: 'system', at }, orgId, indexId)
                    next = pushEvent(next, { objective_code: code, event_type: 'suggested', score_from: cur.score, score_to: score, tier: sug.tier, actor: 'system', occurred_at: at, reason: sug.reason }, orgId, indexId)
                  }
                  states[code] = { ...cur, suggested_score: score, suggested_tier: sug.tier, suggested_at: at, suggested_reason: sug.reason }
                } else {
                  states[code] = { ...cur, suggested_score: null, suggested_tier: null, suggested_at: null, suggested_reason: sug.reason }
                }
                return
              }
              const sug = suggestLevel(code, ctx)
              if (sug.manual_only) {
                states[code] = cur
                return
              }
              if (cur.status === 'unset' || cur.status === 'suggested') {
                if (sug.level) {
                  if (cur.suggested_level !== sug.level) {
                    written++
                    next = pushHistory(next, { objective_code: code, kind: 'suggestion', text: `System suggested ${sug.level} · ${levelName(index, sug.level)}`, actor: 'system', at }, orgId, indexId)
                    const down = cur.suggested_level != null && levelNum(sug.level) < levelNum(cur.suggested_level)
                    const coverageDown = down && expanded && objectiveHasEstateScope(code)
                    next = pushEvent(next, { objective_code: code, event_type: 'suggested', from_level: cur.suggested_level, to_level: sug.level, actor: 'system', occurred_at: at, reason: sug.reason, cause: down ? (coverageDown ? 'coverage' : 'posture') : null }, orgId, indexId)
                    changes.push({ group: 'levels', kind: coverageDown ? 'coverage' : 'suggested', objective_code: code, from_level: cur.suggested_level, to_level: sug.level })
                  }
                  states[code] = { ...cur, status: 'suggested', suggested_level: sug.level, suggested_at: at, suggested_reason: sug.reason }
                } else {
                  if (cur.suggested_level != null) next = pushEvent(next, { objective_code: code, event_type: 'suggested', from_level: cur.suggested_level, to_level: null, actor: 'system', occurred_at: at, reason: sug.reason }, orgId, indexId)
                  states[code] = { ...cur, status: 'unset', suggested_level: null, suggested_at: null, suggested_reason: sug.reason }
                }
              } else {
                // confirmed / declared: only a *higher* differing suggestion is surfaced.
                if (sug.level && levelNum(sug.level) > levelNum(cur.level)) {
                  if (cur.suggested_level !== sug.level) {
                    written++
                    next = pushHistory(next, { objective_code: code, kind: 'suggestion', text: `Confirmed ${cur.level} · system now suggests ${sug.level}`, actor: 'system', at }, orgId, indexId)
                    next = pushEvent(next, { objective_code: code, event_type: 'suggested', from_level: cur.level, to_level: sug.level, actor: 'system', occurred_at: at, reason: sug.reason }, orgId, indexId)
                    changes.push({ group: 'levels', kind: 'flag', objective_code: code, from_level: cur.level, to_level: sug.level, confirmed_level: cur.level })
                  }
                  states[code] = { ...cur, suggested_level: sug.level, suggested_at: at, suggested_reason: sug.reason }
                } else {
                  if (cur.suggested_level != null) next = pushEvent(next, { objective_code: code, event_type: 'suggested', from_level: cur.suggested_level, to_level: null, actor: 'system', occurred_at: at, reason: sug.reason }, orgId, indexId)
                  states[code] = { ...cur, suggested_level: null, suggested_at: null, suggested_reason: sug.reason }
                }
              }
            })
            run = { ...run, finished_at: now(), status: 'succeeded', evidence_created: fresh.length, suggestions_written: written, changes, evidence_skipped_archived: skippedArchived }
            return {
              ...next,
              states,
              sync_runs: [run, ...next.sync_runs].slice(0, 50),
              settings: { ...next.settings, discovery_facts: facts.discovery, playbook_facts: facts.playbook ?? next.settings.playbook_facts ?? null },
              next_scheduled_snapshot: next.next_scheduled_snapshot ?? nextScheduledSnapshot(next.settings.snapshot_rule),
            }
          }),
        }))
        void actor
        return run
      },

      failSync: (orgId, indexId, error, trigger) =>
        set((st) => ({
          scopes: withScope(st.scopes, orgId, indexId, (s) => ({
            ...s,
            sync_runs: [
              { id: uid('sync'), org_id: orgId, index_id: indexId, started_at: now(), finished_at: now(), status: 'failed' as const, error, discovery_connected: false, evidence_created: 0, suggestions_written: 0, trigger },
              ...s.sync_runs,
            ].slice(0, 50),
          })),
        })),

      confirmLevel: (orgId, indexId, code, level, note, actor, cause) =>
        set((st) => ({
          scopes: withScope(st.scopes, orgId, indexId, (s) => {
            const index = getIndex(indexId)
            const cur = s.states[code] ?? emptyState(orgId, indexId, code)
            const at = now()
            const suggested = cur.suggested_level
            const accepted = suggested != null && suggested === level
            const status: ObjectiveState['status'] = accepted ? 'confirmed' : 'declared'
            const fromLevel = cur.status === 'unset' ? null : cur.level
            const downward = fromLevel != null && levelNum(level) < levelNum(fromLevel)
            const nextState: ObjectiveState = {
              ...cur,
              level,
              status,
              set_by: actor,
              set_at: at,
              note: note || null,
              suggested_level: null,
              suggested_at: null,
            }
            let next: ScopeData = { ...s, states: { ...s.states, [code]: nextState } }
            next = pushHistory(
              next,
              {
                objective_code: code,
                kind: accepted ? 'confirm' : 'manual',
                text: accepted
                  ? `Confirmed ${level} · ${levelName(index, level)}`
                  : `Set ${level} · ${levelName(index, level)}${downward ? ` (down from ${fromLevel}${cause === 'coverage' ? ' · coverage-driven' : ''})` : ''}${suggested ? ` (overriding suggested ${suggested})` : ''}${note ? ` · "${note}"` : ''}`,
                actor,
                at,
              },
              orgId,
              indexId
            )
            next = pushEvent(next, { objective_code: code, event_type: accepted ? 'confirmed' : suggested ? 'overridden' : 'declared', status, from_level: fromLevel, to_level: level, actor, occurred_at: at, note: note || null, basis: cur.basis, cause: downward ? (cause ?? 'posture') : null }, orgId, indexId)
            const ev: AnalyticsEvent = accepted
              ? { name: 'readiness.level_confirmed', objective_code: code, level }
              : { name: 'readiness.level_overridden', objective_code: code, level, from: suggested }
            return { ...next, analytics: [...next.analytics, ev] }
          }),
        })),

      confirmScore: (orgId, indexId, code, input, note, actor) =>
        set((st) => ({
          scopes: withScope(st.scopes, orgId, indexId, (s) => {
            const index = getIndex(indexId)
            const cur = s.states[code] ?? emptyState(orgId, indexId, code)
            const at = now()
            const m = tierCount(index, code)
            const score = Math.round(Math.max(0, Math.min(10, input.score)) * 10) / 10
            const accepted = cur.suggested_score != null && cur.suggested_score === score
            const status: ObjectiveState['status'] = accepted ? 'confirmed' : 'declared'
            const fromScore = cur.status === 'unset' ? null : cur.score
            const nextState: ObjectiveState = {
              ...cur,
              status,
              score,
              tier: input.tier,
              set_by: actor,
              set_at: at,
              note: note || null,
              suggested_score: null,
              suggested_tier: null,
              suggested_at: null,
            }
            let next: ScopeData = { ...s, states: { ...s.states, [code]: nextState } }
            const label = input.tier != null ? `tier ${input.tier} of ${m} · ${score.toFixed(1)}` : `score ${score.toFixed(1)}`
            next = pushHistory(next, { objective_code: code, kind: accepted ? 'confirm' : 'manual', text: `${accepted ? 'Confirmed' : 'Set'} ${label}${note ? ` · "${note}"` : ''}`, actor, at }, orgId, indexId)
            next = pushEvent(next, { objective_code: code, event_type: accepted ? 'confirmed' : cur.suggested_score != null ? 'overridden' : 'declared', status, score_from: fromScore, score_to: score, tier: input.tier, actor, occurred_at: at, note: note || null, basis: cur.basis }, orgId, indexId)
            return next
          }),
        })),

      setDetails: (orgId, indexId, code, patch, actor) =>
        set((st) => ({
          scopes: withScope(st.scopes, orgId, indexId, (s) => {
            const cur = s.states[code] ?? emptyState(orgId, indexId, code)
            let next: ScopeData = { ...s, states: { ...s.states, [code]: { ...cur, ...patch } } }
            const at = now()
            const bits = Object.entries(patch)
              .filter(([, v]) => v !== undefined)
              .map(([k, v]) => `${k.replace('_', ' ')}: ${v ?? '—'}`)
            if (patch.owner !== undefined && patch.owner !== cur.owner) next = pushEvent(next, { objective_code: code, event_type: 'owner_set', actor, occurred_at: at, note: patch.owner }, orgId, indexId)
            if (patch.due_at !== undefined && patch.due_at !== cur.due_at) next = pushEvent(next, { objective_code: code, event_type: 'due_set', actor, occurred_at: at, note: patch.due_at }, orgId, indexId)
            return bits.length ? pushHistory(next, { objective_code: code, kind: 'manual', text: `Details updated · ${bits.join(', ')}`, actor, at }, orgId, indexId) : next
          }),
        })),

      setBasis: (orgId, indexId, code, basis, actor) =>
        set((st) => ({
          scopes: withScope(st.scopes, orgId, indexId, (s) => {
            const cur = s.states[code] ?? emptyState(orgId, indexId, code)
            if (cur.basis === basis) return s
            const at = now()
            let next: ScopeData = { ...s, states: { ...s.states, [code]: { ...cur, basis } } }
            next = pushEvent(next, { objective_code: code, event_type: 'basis_set', basis, actor, occurred_at: at }, orgId, indexId)
            return pushHistory(next, { objective_code: code, kind: 'manual', text: `Basis of level · ${basis === 'csa_report' ? 'CSA QRI report attached' : 'Internal judgement'}`, actor, at }, orgId, indexId)
          }),
        })),

      attachCsaReport: (orgId, indexId, code, fileName, actor) => {
        const ev = get().addEvidence(orgId, indexId, { objective_code: code, title: 'CSA QRI report', summary: "The PDF produced by CSA's official questionnaire.", link: null, occurred_at: now(), kind: 'file', file_name: fileName }, actor)
        get().setBasis(orgId, indexId, code, 'csa_report', actor)
        return ev
      },

      addEvidence: (orgId, indexId, input, actor) => {
        const ev: Evidence = {
          id: uid('ev'),
          org_id: orgId,
          index_id: indexId,
          objective_code: input.objective_code,
          source: 'manual',
          external_id: null,
          title: input.title,
          summary: input.summary,
          link: input.link,
          occurred_at: input.occurred_at,
          added_by: actor,
          archived_at: null,
          kind: input.kind,
          file_name: input.file_name ?? null,
        }
        set((st) => ({
          scopes: withScope(st.scopes, orgId, indexId, (s) => {
            const at = now()
            let next: ScopeData = { ...s, evidence: [...s.evidence, ev] }
            next = pushHistory(next, { objective_code: ev.objective_code, kind: 'evidence', text: `Evidence added · ${ev.title}`, actor, at }, orgId, indexId)
            next = pushEvent(next, { objective_code: ev.objective_code, event_type: 'evidence_added', evidence_id: ev.id, actor, occurred_at: at }, orgId, indexId)
            if (input.suggest_level) {
              const index = getIndex(indexId)
              const cur = next.states[ev.objective_code] ?? emptyState(orgId, indexId, ev.objective_code)
              if (cur.status === 'unset' || cur.status === 'suggested' || levelNum(input.suggest_level) > levelNum(cur.level)) {
                next = {
                  ...next,
                  states: {
                    ...next.states,
                    [ev.objective_code]: {
                      ...cur,
                      status: cur.status === 'unset' ? 'suggested' : cur.status,
                      suggested_level: input.suggest_level,
                      suggested_at: at,
                      suggested_reason: `Suggested by ${actor} with evidence "${ev.title}".`,
                    },
                  },
                }
                next = pushHistory(next, { objective_code: ev.objective_code, kind: 'suggestion', text: `${actor} suggested ${input.suggest_level} · ${levelName(index, input.suggest_level)} with evidence`, actor, at }, orgId, indexId)
                next = pushEvent(next, { objective_code: ev.objective_code, event_type: 'suggested', from_level: cur.suggested_level, to_level: input.suggest_level, evidence_id: ev.id, actor, occurred_at: at }, orgId, indexId)
              }
            }
            return { ...next, analytics: [...next.analytics, { name: 'readiness.evidence_added', objective_code: ev.objective_code, source: 'manual' }] }
          }),
        }))
        return ev
      },

      archiveEvidence: (orgId, indexId, evidenceId, actor) =>
        set((st) => ({
          scopes: withScope(st.scopes, orgId, indexId, (s) => {
            const target = s.evidence.find((e) => e.id === evidenceId)
            if (!target || target.archived_at) return s
            const at = now()
            let next: ScopeData = { ...s, evidence: s.evidence.map((e) => (e.id === evidenceId ? { ...e, archived_at: at } : e)) }
            next = pushEvent(next, { objective_code: target.objective_code, event_type: 'evidence_archived', evidence_id: target.id, actor, occurred_at: at }, orgId, indexId)
            return pushHistory(next, { objective_code: target.objective_code, kind: 'manual', text: `Evidence archived · ${target.title}`, actor, at }, orgId, indexId)
          }),
        })),

      restoreEvidence: (orgId, indexId, evidenceId, actor) =>
        set((st) => ({
          scopes: withScope(st.scopes, orgId, indexId, (s) => {
            const target = s.evidence.find((e) => e.id === evidenceId)
            // The server rejects a restore of an item that was never archived (I5 Note 6).
            if (!target || !target.archived_at) return s
            const at = now()
            let next: ScopeData = { ...s, evidence: s.evidence.map((e) => (e.id === evidenceId ? { ...e, archived_at: null } : e)) }
            next = pushEvent(next, { objective_code: target.objective_code, event_type: 'evidence_restored', evidence_id: target.id, actor, occurred_at: at }, orgId, indexId)
            return pushHistory(next, { objective_code: target.objective_code, kind: 'manual', text: `Evidence restored · ${target.title}`, actor, at }, orgId, indexId)
          }),
        })),

      appendSyncChanges: (orgId, indexId, runId, changes) =>
        set((st) => ({
          scopes: withScope(st.scopes, orgId, indexId, (s) => ({ ...s, sync_runs: s.sync_runs.map((r) => (r.id === runId ? { ...r, changes: [...(r.changes ?? []), ...changes] } : r)) })),
        })),

      markSyncViewed: (runId) => set((st) => ({ syncViewed: { ...st.syncViewed, [runId]: now() } })),

      saveSnapshot: (orgId, indexId, input, actor) => {
        const index = getIndex(indexId)
        const scale = assertScale(index)
        const at = now()
        const id = uid('snap')
        const key = scopeKey(orgId, indexId)
        const current = get().scopes[key] ?? emptyScope()
        // Review gates run server-side on save as well as in the modal (Note 5).
        const gates = reviewGates(index, current)
        if (gates.blocking.length && !input.force) throw new ReviewGateError(gates.blocking.map((b) => b.code), gates.blocking)
        let created: Snapshot | null = null
        set((st) => ({
          scopes: withScope(st.scopes, orgId, indexId, (s) => {
            const previous = s.snapshots[s.snapshots.length - 1]
            const items: SnapshotItem[] = allObjectives(index).map(({ objective }) => {
              const state = s.states[objective.code]
              const prev = previous?.items.find((i) => i.objective_code === objective.code)
              return {
                snapshot_id: id,
                objective_code: objective.code,
                level: reportedLevel(state),
                status: state?.status ?? 'unset',
                evidence_ids: s.evidence.filter((e) => e.objective_code === objective.code && !e.archived_at).map((e) => e.id),
                basis: state?.basis ?? 'internal',
                score: scale === 'score_0_10' ? reportedScore(state) : null,
                previous_level: previous ? (prev?.level ?? 'L0') : null,
              }
            })
            const score = scale === 'levels' ? computeScore(items.map((i) => i.level)) : (computeIndexScore(index, (c) => items.find((i) => i.objective_code === c)?.score ?? null).overall ?? 0)
            const live = scale === 'levels' ? computeScore(allObjectives(index).map(({ objective }) => liveLevel(s.states[objective.code]))) : (computeIndexScore(index, (c) => liveScore(s.states[c])).overall ?? 0)
            const lastSync = s.sync_runs.find((r) => r.status === 'succeeded')
            const asOfRaw = input.as_of ? new Date(input.as_of) : new Date(at)
            const minAsOf = previous ? new Date(previous.created_at) : null
            const asOf = minAsOf && asOfRaw < minAsOf ? minAsOf : asOfRaw
            const snap: Snapshot = {
              id,
              org_id: orgId,
              index_id: indexId,
              index_version: index.version,
              name: input.name,
              note: input.note,
              score,
              live_score: live,
              created_by: input.scheduled ? 'system' : actor,
              created_at: at,
              as_of: asOf.toISOString(),
              baseline: s.snapshots.length === 0,
              scheduled: !!input.scheduled,
              items,
              discovery_summary: discoverySummaryOf(s.settings.discovery_facts, lastSync?.finished_at ?? lastSync?.started_at ?? null),
              projection: input.projection ?? null,
            }
            created = snap
            let next: ScopeData = { ...s, snapshots: [...s.snapshots, snap] }
            next = pushHistory(next, { objective_code: null, kind: 'snapshot', text: `Assessment saved · ${snap.name}${snap.baseline ? ' · baseline' : ''}`, actor: snap.created_by, at }, orgId, indexId)
            next = pushEvent(next, { objective_code: null, event_type: 'assessment_saved', actor: snap.created_by, occurred_at: at, note: snap.id }, orgId, indexId)
            return {
              ...next,
              next_scheduled_snapshot: nextScheduledSnapshot(next.settings.snapshot_rule),
              analytics: [...next.analytics, { name: 'readiness.assessment_saved', snapshot_id: id, baseline: snap.baseline }],
            }
          }),
        }))
        return created!
      },

      snoozeReminder: (orgId, indexId) =>
        set((st) => ({
          scopes: withScope(st.scopes, orgId, indexId, (s) => ({ ...s, next_scheduled_snapshot: nextScheduledSnapshot(s.settings.snapshot_rule) })),
        })),

      setSnapshotRule: (orgId, indexId, rule) =>
        set((st) => ({
          scopes: withScope(st.scopes, orgId, indexId, (s) => ({
            ...s,
            settings: { ...s.settings, snapshot_rule: rule },
            next_scheduled_snapshot: nextScheduledSnapshot(rule),
          })),
        })),

      createPlaybookAction: (orgId, indexId, input, actor) => {
        const at = now()
        const action: LocalPlaybookAction = {
          id: uid('act'),
          title: input.title,
          objective_code: input.objective_code,
          transition: input.transition,
          bullet_index: input.bullet_index,
          inventory_id: input.inventory_id ?? null,
          owner: input.owner,
          due_at: input.due_at,
          status: input.owner ? 'in_progress' : 'open',
          created_at: at,
          updated_at: at,
        }
        const link: RecommendationLink = {
          id: uid('lnk'),
          org_id: orgId,
          index_id: indexId,
          objective_code: input.objective_code,
          transition: input.transition,
          bullet_index: input.bullet_index,
          playbook_action_id: action.id,
          action_title: action.title,
          owner: action.owner,
          due_at: action.due_at,
          status: action.status,
          created_at: at,
        }
        set((st) => ({
          scopes: withScope(st.scopes, orgId, indexId, (s) => {
            const next = { ...s, playbook_actions: [...s.playbook_actions, action], links: [...s.links, link] }
            return pushHistory(next, { objective_code: input.objective_code, kind: 'playbook', text: `Playbook action created · ${action.title}${action.owner ? ` · assigned to ${action.owner}` : ''}`, actor, at }, orgId, indexId)
          }),
        }))
        return link
      },

      setLinkStatus: (orgId, indexId, linkId, status, actor) =>
        set((st) => ({
          scopes: withScope(st.scopes, orgId, indexId, (s) => {
            const link = s.links.find((l) => l.id === linkId)
            if (!link) return s
            const at = now()
            const next = {
              ...s,
              links: s.links.map((l) => (l.id === linkId ? { ...l, status } : l)),
              playbook_actions: s.playbook_actions.map((a) => (a.id === link.playbook_action_id ? { ...a, status, updated_at: at } : a)),
            }
            return pushHistory(next, { objective_code: link.objective_code, kind: 'playbook', text: `Playbook action ${status.replace('_', ' ')} · ${link.action_title}`, actor, at }, orgId, indexId)
          }),
        })),

      upsertDraft: (orgId, indexId, draft) =>
        set((st) => ({
          scopes: withScope(st.scopes, orgId, indexId, (s) => ({
            ...s,
            drafts: s.drafts.some((d) => d.id === draft.id) ? s.drafts.map((d) => (d.id === draft.id ? draft : d)) : [...s.drafts, draft],
          })),
        })),

      deleteDraft: (orgId, indexId, draftId) =>
        set((st) => ({
          scopes: withScope(st.scopes, orgId, indexId, (s) => ({ ...s, drafts: s.drafts.filter((d) => d.id !== draftId) })),
        })),

      recordExport: (orgId, indexId, rec) => {
        const full: ExportRecord = { id: uid('exp'), org_id: orgId, index_id: indexId, ...rec }
        set((st) => ({
          scopes: withScope(st.scopes, orgId, indexId, (s) => ({
            ...s,
            exports: [full, ...s.exports],
            analytics: [...s.analytics, { name: 'readiness.export_completed', formats: rec.formats, snapshot_id: rec.snapshot_id }],
          })),
        }))
        return full
      },

      recordDependencyChange: (orgId, indexId, input) => {
        const written: ObjectiveEvent[] = []
        set((st) => ({
          scopes: withScope(st.scopes, orgId, indexId, (s) => {
            let next = s
            const at = now()
            input.codes.forEach((code) => {
              next = pushEvent(next, { objective_code: code, event_type: 'dependency_changed', actor: 'system', occurred_at: at, dependency: input.dependency, note: input.text }, orgId, indexId)
              const ev = next.events[next.events.length - 1]
              written.push(ev)
              next = pushHistory(next, { objective_code: code, kind: 'dependency', text: input.text, actor: 'system', at, event_id: ev.id, detail: 'from catalogue' }, orgId, indexId)
            })
            return next
          }),
        }))
        return written
      },

      setStepChoice: (orgId, indexId, code, transition, bullet, choice, actor) =>
        set((st) => ({
          scopes: withScope(st.scopes, orgId, indexId, (s) => {
            const key = stepKey(code, transition, bullet)
            const choices = { ...(s.step_choices ?? {}) }
            if (choice) choices[key] = { ...choice, at: now(), by: actor }
            else delete choices[key]
            const next = { ...s, step_choices: choices }
            if (!choice) return next
            const label = choice.option === 'wait' ? 'Keep tracking the vendor date' : choice.option === 'pilot' ? 'Pilot on a PQC-ready alternative' : `Waiver recorded · step deferred, justified${choice.waiver_expiry ? ` until ${choice.waiver_expiry.slice(0, 10)}` : ''}`
            return { ...pushHistory(next, { objective_code: code, kind: 'manual', text: `Step ${bullet + 1} · ${label}`, actor, at: now() }, orgId, indexId), analytics: [...next.analytics, { name: 'readiness.dependency_option_chosen', option: choice.option }] }
          }),
        })),

      setScopeThreshold: (orgId, indexId, threshold) =>
        set((st) => ({ scopes: withScope(st.scopes, orgId, indexId, (s) => ({ ...s, settings: { ...s.settings, scope_threshold: threshold } })) })),

      track: (orgId, indexId, ev) =>
        set((st) => ({ scopes: withScope(st.scopes, orgId, indexId, (s) => ({ ...s, analytics: [...s.analytics, ev] })) })),

      resetScope: (orgId, indexId) =>
        set((st) => ({ scopes: { ...st.scopes, [scopeKey(orgId, indexId)]: emptyScope() } })),
    }),
    {
      name: 'qtz-readiness',
      version: 4,
      // v1 → v2 (Iteration 2): scopes gain the event log, states a basis and
      // score fields, assessments an as-of date and Discovery summary. The log
      // is never backfilled from assessments; older scopes start it from now.
      // v3 → v4: the demo tenant was withdrawn; its scopes are dropped.
      migrate: (persisted, version) => {
        const st = persisted as { selectedIndexId?: string; viewMode?: ViewMode; scopes?: Record<string, ScopeData> }
        const withoutDemo = () =>
          Object.fromEntries(Object.entries(st.scopes ?? {}).filter(([k]) => !k.startsWith(RETIRED_DEMO_SCOPE_PREFIX)))
        if (version >= 2) return { ...st, scopes: withoutDemo() } as unknown as ReadinessStore
        const scopes = Object.fromEntries(
          Object.entries(st.scopes ?? {}).map(([k, s]) => [
            k,
            {
              ...emptyScope(),
              ...s,
              events: s.events ?? [],
              states: Object.fromEntries(Object.entries(s.states ?? {}).map(([c, v]) => [c, { ...emptyState(v.org_id, v.index_id, c), ...v }])),
              snapshots: (s.snapshots ?? []).map((snap) => ({ ...snap, as_of: snap.as_of ?? snap.created_at, discovery_summary: snap.discovery_summary ?? null })),
            },
          ])
        )
        return { selectedIndexId: st.selectedIndexId ?? DEFAULT_INDEX_ID, viewMode: st.viewMode ?? 'cards', scopes } as unknown as ReadinessStore
      },
    }
  )
)

// Convenience selectors ------------------------------------------------------

export function useScope(orgId: string, indexId: string): ScopeData {
  const scope = useReadinessStore((s) => s.scopes[scopeKey(orgId, indexId)])
  return scope ?? EMPTY
}
const EMPTY = emptyScope()

export function lastSuccessfulSync(scope: ScopeData): SyncRun | undefined {
  return scope.sync_runs.find((r) => r.status === 'succeeded')
}
export function lastSync(scope: ScopeData): SyncRun | undefined {
  return scope.sync_runs[0]
}
export function evidenceFor(scope: ScopeData, code: string): Evidence[] {
  return scope.evidence.filter((e) => e.objective_code === code && !e.archived_at)
}
export function liveLevels(index: IndexDef, scope: ScopeData): Record<string, LevelCode> {
  return Object.fromEntries(allObjectives(index).map(({ objective }) => [objective.code, liveLevel(scope.states[objective.code])]))
}
// Suggested name for the next assessment: "{Qn} {yyyy} assessment" (I2 §8.1).
// Never a name already used (I5 §12.1): the second in a quarter defaults to
// "… assessment 2", and so on.
export function suggestedAssessmentName(date = new Date(), existing: string[] = []): string {
  const base = `Q${Math.floor(date.getMonth() / 3) + 1} ${date.getFullYear()} assessment`
  const used = new Set(existing.map((n) => n.trim().toLowerCase()))
  if (!used.has(base.toLowerCase())) return base
  for (let i = 2; i < 1000; i++) {
    const candidate = `${base} ${i}`
    if (!used.has(candidate.toLowerCase())) return candidate
  }
  return base
}
